import { AlertCircleIcon, InboxIcon } from "lucide-react";
import type * as React from "react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { errorMessage } from "@/lib/http";
import { cn } from "@/lib/utils";

/**
 * Listelerin yükleniyor / hata / boş üç durumu.
 *
 * Her ekranda elle yazılınca üçünden biri hep unutuluyordu — özellikle hata
 * durumu: sorgu patladığında ızgara boş görünüyor ve kullanıcı kaydın
 * silindiğini sanıyordu.
 */

interface DataStateProps {
  isLoading: boolean;
  error: unknown;
  isEmpty: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;
  onRetry?: () => void;
  /** Yüklenirken gösterilecek iskelet satır sayısı. */
  skeletonRows?: number;
  children: React.ReactNode;
}

export function DataState({
  isLoading,
  error,
  isEmpty,
  emptyTitle = "Kayıt yok",
  emptyDescription,
  emptyAction,
  onRetry,
  skeletonRows = 5,
  children,
}: DataStateProps) {
  if (isLoading) {
    return (
      <div className="space-y-2 p-1" aria-busy="true" aria-live="polite">
        {Array.from({ length: skeletonRows }, (_, index) => (
          <Skeleton key={index} className="h-11 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <StatePanel
        icon={<AlertCircleIcon className="text-destructive size-6" />}
        title="Veriler yüklenemedi"
        description={errorMessage(error)}
        action={
          onRetry && (
            <Button variant="outline" size="sm" onClick={onRetry}>
              Tekrar dene
            </Button>
          )
        }
      />
    );
  }

  if (isEmpty) {
    return (
      <StatePanel
        icon={<InboxIcon className="text-muted-foreground size-6" />}
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
      />
    );
  }

  return <>{children}</>;
}

interface StatePanelProps {
  icon: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
}

export function StatePanel({ icon, title, description, action, className }: StatePanelProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 px-6 py-14 text-center",
        className,
      )}
    >
      <div className="bg-muted flex size-12 items-center justify-center rounded-full">{icon}</div>

      <div className="space-y-1">
        <p className="font-medium">{title}</p>
        {description && (
          <p className="text-muted-foreground max-w-sm text-sm text-balance">{description}</p>
        )}
      </div>

      {action}
    </div>
  );
}
